import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { authFetch, getAuth, getToken, isAdmin } from "../../auth";
import { hubUrl } from "../../appConfig";
import { ActionModal } from "../Common/ActionModal";
import {
  formatCoordinate,
  formatDateTimeText,
  formatSpeedText,
  getDefaultDateTimeRange,
  toUtcDateTimeQueryValue
} from "./logHelpers";
import { fetchCustomerOptions, fetchDeviceOptions, fetchGroupOptions, formatCustomerLabel, formatDeviceLabel } from "./logFilterOptions";

const PAGE_SIZE = 20;

function normalizePage(value) {
  const page = Number.parseInt(value || "", 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
}

function normalizeClearStateQuery(value) {
  return ["processed", "unprocessed", "unknown"].includes(value || "") ? value : "";
}

function readQueryState(search, admin) {
  const qs = new URLSearchParams(search || "");
  const defaultTimeRange = getDefaultDateTimeRange();

  return {
    page: normalizePage(qs.get("page")),
    alarmType: qs.get("alarmType") || "",
    deviceNo: qs.get("deviceNo") || "",
    keyword: qs.get("keyword") || "",
    clearState: normalizeClearStateQuery(qs.get("clearState")),
    customerId: admin ? (qs.get("customerId") || "") : "",
    groupId: qs.get("groupId") || "",
    startTime: qs.get("startTime") || defaultTimeRange.startTime,
    endTime: qs.get("endTime") || defaultTimeRange.endTime
  };
}

function buildQueryState(params) {
  return {
    page: params.page,
    alarmType: params.alarmType,
    deviceNo: params.deviceNo,
    keyword: params.keyword,
    clearState: params.clearState,
    customerId: params.customerId,
    groupId: params.groupId,
    startTime: params.startTime,
    endTime: params.endTime
  };
}

function areQueryStatesEqual(left, right) {
  return left.page === right.page
    && left.alarmType === right.alarmType
    && left.deviceNo === right.deviceNo
    && left.keyword === right.keyword
    && left.clearState === right.clearState
    && left.customerId === right.customerId
    && left.groupId === right.groupId
    && left.startTime === right.startTime
    && left.endTime === right.endTime;
}

function buildBrowserSearch(queryState, admin) {
  const qs = new URLSearchParams();
  qs.set("page", String(queryState.page));

  if (queryState.alarmType !== "") qs.set("alarmType", queryState.alarmType);
  if (queryState.deviceNo.trim()) qs.set("deviceNo", queryState.deviceNo.trim());
  if (queryState.keyword.trim()) qs.set("keyword", queryState.keyword.trim());
  if (queryState.clearState !== "") qs.set("clearState", queryState.clearState);
  if (admin && queryState.customerId.trim()) qs.set("customerId", queryState.customerId.trim());
  if (queryState.groupId.trim()) qs.set("groupId", queryState.groupId.trim());
  if (queryState.startTime) qs.set("startTime", queryState.startTime);
  if (queryState.endTime) qs.set("endTime", queryState.endTime);

  return qs.toString();
}

function getClearStateBadgeClass(clearState) {
  switch (clearState) {
    case "processed":
      return "bg-secondary";
    case "unknown":
      return "bg-warning text-dark";
    default:
      return "bg-danger";
  }
}

function getClearStateText(row) {
  if (row?.clearStateText) return row.clearStateText;
  if (row?.isCleared === true) return "已处理";
  if (row?.isCleared === false) return "未处理";
  return "状态未知";
}

function normalizeClearState(row) {
  if (row?.clearState) return row.clearState;
  if (row?.isCleared === true) return "processed";
  if (row?.isCleared === false) return "unprocessed";
  return "unknown";
}

function buildMonitorUrl(row, returnTo) {
  const qs = new URLSearchParams();

  if (row?.deviceNo) qs.set("deviceNo", row.deviceNo);
  if (row?.vehicleNo) qs.set("vehicleNo", row.vehicleNo);
  if (row?.longitude != null) qs.set("longitude", String(row.longitude));
  if (row?.latitude != null) qs.set("latitude", String(row.latitude));
  if (returnTo) qs.set("returnTo", returnTo);

  const query = qs.toString();
  return query ? `/gps-monitor?${query}` : "/gps-monitor";
}

export function AlarmLogPage() {
  const admin = isAdmin();
  const auth = getAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialQueryState = readQueryState(location.search, admin);
  const myCustomerId = auth?.customerId ? String(auth.customerId) : "";
  const canUseMineShortcut = !admin || !!myCustomerId;

  const [alarmType, setAlarmType] = useState(initialQueryState.alarmType);
  const [deviceNo, setDeviceNo] = useState(initialQueryState.deviceNo);
  const [keyword, setKeyword] = useState(initialQueryState.keyword);
  const [clearState, setClearState] = useState(initialQueryState.clearState);
  const [customerId, setCustomerId] = useState(initialQueryState.customerId);
  const [groupId, setGroupId] = useState(initialQueryState.groupId);
  const [startTime, setStartTime] = useState(initialQueryState.startTime);
  const [endTime, setEndTime] = useState(initialQueryState.endTime);

  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [optionError, setOptionError] = useState("");

  const [page, setPage] = useState(initialQueryState.page);
  const [queryVersion, setQueryVersion] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [clearModal, setClearModal] = useState({ open: false, row: null });

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const optionCustomerId = admin ? customerId : myCustomerId;
  const selectedDeviceExists = devices.some((device) => device.deviceNo === deviceNo);
  const currentQueryState = buildQueryState({
    page,
    alarmType,
    deviceNo,
    keyword,
    clearState,
    customerId,
    groupId,
    startTime,
    endTime
  });

  useEffect(() => {
    if (!admin) return;
    loadCustomerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  useEffect(() => {
    loadLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, queryVersion]);

  useEffect(() => {
    const nextQueryState = readQueryState(location.search, admin);
    if (areQueryStatesEqual(currentQueryState, nextQueryState)) {
      return;
    }

    setAlarmType(nextQueryState.alarmType);
    setDeviceNo(nextQueryState.deviceNo);
    setKeyword(nextQueryState.keyword);
    setClearState(nextQueryState.clearState);
    setCustomerId(nextQueryState.customerId);
    setGroupId(nextQueryState.groupId);
    setStartTime(nextQueryState.startTime);
    setEndTime(nextQueryState.endTime);
    setPage(nextQueryState.page);
    setQueryVersion((v) => v + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, admin]);

  useEffect(() => {
    loadGroupOptions(optionCustomerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionCustomerId]);

  useEffect(() => {
    loadDeviceOptions(optionCustomerId, groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionCustomerId, groupId]);

  useEffect(() => {
    const token = getToken();
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl("/hubs/gps"), {
        accessTokenFactory: () => token
      })
      .withAutomaticReconnect()
      .build();

    const refresh = () => {
      setQueryVersion((v) => v + 1);
    };

    connection.on("AlarmLogCreated", refresh);
    connection.on("AlarmLogUpdated", refresh);

    connection.start().catch(() => {});
    return () => {
      connection.stop();
    };
  }, []);

  async function loadCustomerOptions() {
    try {
      setCustomers(await fetchCustomerOptions());
      setOptionError("");
    } catch (err) {
      setCustomers([]);
      setOptionError(err?.message || "客户列表加载失败");
    }
  }

  async function loadGroupOptions(nextCustomerId) {
    if (admin && !nextCustomerId) {
      setGroups([]);
      return;
    }

    try {
      setGroups(await fetchGroupOptions(nextCustomerId));
      setOptionError("");
    } catch (err) {
      setGroups([]);
      setOptionError(err?.message || "分组列表加载失败");
    }
  }

  async function loadDeviceOptions(nextCustomerId, nextGroupId) {
    if (admin && !nextCustomerId) {
      setDevices([]);
      return;
    }

    try {
      setDevices(await fetchDeviceOptions({ customerId: nextCustomerId, groupId: nextGroupId }));
      setOptionError("");
    } catch (err) {
      setDevices([]);
      setOptionError(err?.message || "车辆列表加载失败");
    }
  }

  function onCustomerChange(value) {
    setCustomerId(value);
    setGroupId("");
    setDeviceNo("");
  }

  function onGroupChange(value) {
    setGroupId(value);
    setDeviceNo("");
  }

  async function loadLogs(targetPage) {
    setLoading(true);
    setError("");

    try {
      const nextSearch = buildBrowserSearch({ ...currentQueryState, page: targetPage }, admin);
      const currentSearch = (location.search || "").replace(/^\?/, "");
      if (currentSearch !== nextSearch) {
        navigate(nextSearch ? `?${nextSearch}` : "", { replace: true });
      }

      const qs = new URLSearchParams({
        page: String(targetPage),
        pageSize: String(PAGE_SIZE)
      });

      if (alarmType !== "") qs.set("alarmType", alarmType);
      if (deviceNo.trim()) qs.set("deviceNo", deviceNo.trim());
      if (keyword.trim()) qs.set("keyword", keyword.trim());
      if (clearState !== "") qs.set("clearState", clearState);
      if (admin && customerId.trim()) qs.set("customerId", customerId.trim());
      if (groupId.trim()) qs.set("groupId", groupId.trim());

      const startUtc = toUtcDateTimeQueryValue(startTime);
      const endUtc = toUtcDateTimeQueryValue(endTime);
      if (startUtc) qs.set("startTimeUtc", startUtc);
      if (endUtc) qs.set("endTimeUtc", endUtc);

      const res = await authFetch(`/api/logs/alarms?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "报警日志查询失败");
      }

      const data = await res.json();
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.message || "报警日志查询失败");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  function onReset() {
    setAlarmType("");
    setDeviceNo("");
    setKeyword("");
    setClearState("");
    setCustomerId("");
    setGroupId("");
    const nextTimeRange = getDefaultDateTimeRange();
    setStartTime(nextTimeRange.startTime);
    setEndTime(nextTimeRange.endTime);
    setError("");
    setMessage("");
    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  function applyQuickFilter(filterKey) {
    setError("");
    setMessage("");

    switch (filterKey) {
      case "mine-unprocessed":
        setAlarmType("");
        setClearState("unprocessed");
        setGroupId("");
        setDeviceNo("");
        if (admin && myCustomerId) {
          setCustomerId(myCustomerId);
        }
        break;
      case "unprocessed-emergency":
        setAlarmType("0");
        setClearState("unprocessed");
        break;
      case "unprocessed-security":
        setAlarmType("1");
        setClearState("unprocessed");
        break;
      case "unprocessed-overspeed":
        setAlarmType("2");
        setClearState("unprocessed");
        break;
      case "unknown-state":
        setAlarmType("");
        setClearState("unknown");
        break;
      default:
        break;
    }

    setPage(1);
    setQueryVersion((v) => v + 1);
  }

  function openClearModal(row) {
    setClearModal({ open: true, row });
  }

  function openMonitor(row) {
    const returnSearch = buildBrowserSearch(currentQueryState, admin);
    const returnTo = returnSearch ? `/alarm-logs?${returnSearch}` : "/alarm-logs";
    navigate(buildMonitorUrl(row, returnTo));
  }

  async function submitClearAlarm() {
    const row = clearModal.row;
    if (!row) return;

    setModalSubmitting(true);
    setError("");
    setMessage("");

    try {
      const res = await authFetch(`/api/logs/alarms/${row.id}/clear`, { method: "PUT" });
      if (!res.ok) {
        const txt = await res.text();
        setError(txt || "报警记录处理失败");
        return;
      }

      setClearModal({ open: false, row: null });
      setMessage(`报警记录 ${row.id} 已标记为已处理。`);
      setQueryVersion((v) => v + 1);
    } finally {
      setModalSubmitting(false);
    }
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-primary text-white">报警日志</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={onSearch}>
            <div className="col-md-2">
              <label className="form-label">报警类型</label>
              <select className="form-select" value={alarmType} onChange={(e) => setAlarmType(e.target.value)}>
                <option value="">全部</option>
                <option value="0">紧急报警</option>
                <option value="1">安防报警</option>
                <option value="2">超速报警</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">处理状态</label>
              <select className="form-select" value={clearState} onChange={(e) => setClearState(e.target.value)}>
                <option value="">全部</option>
                <option value="unprocessed">未处理</option>
                <option value="processed">已处理</option>
                <option value="unknown">状态未知</option>
              </select>
            </div>
            {admin && (
              <div className="col-md-2">
                <label className="form-label">客户</label>
                <select className="form-select" value={customerId} onChange={(e) => onCustomerChange(e.target.value)}>
                  <option value="">全部客户</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={String(customer.id)}>
                      {formatCustomerLabel(customer)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label">分组</label>
              <select className="form-select" value={groupId} onChange={(e) => onGroupChange(e.target.value)} disabled={admin && !customerId}>
                <option value="">{admin && !customerId ? "请先选择客户" : "全部分组"}</option>
                {groups.map((group) => (
                  <option key={group.id} value={String(group.id)}>
                    {group.name}{group.deviceCount != null ? ` (${group.deviceCount})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">车辆</label>
              <select className="form-select" value={deviceNo} onChange={(e) => setDeviceNo(e.target.value)} disabled={admin && !customerId}>
                <option value="">{admin && !customerId ? "请先选择客户" : "全部车辆"}</option>
                {deviceNo && !selectedDeviceExists && <option value={deviceNo}>{deviceNo}</option>}
                {devices.map((device) => (
                  <option key={device.id} value={device.deviceNo}>
                    {formatDeviceLabel(device)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">关键字</label>
              <input
                className="form-control"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="地址 / 客户"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">开始时间</label>
              <input className="form-control" type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">结束时间</label>
              <input className="form-control" type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询"}
              </button>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" type="button" onClick={onReset} disabled={loading}>
                重置
              </button>
            </div>
          </form>

          <div className="d-flex flex-wrap gap-2 mt-3">
            {canUseMineShortcut && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => applyQuickFilter("mine-unprocessed")}
                disabled={loading}
              >
                只看我的未处理
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => applyQuickFilter("unprocessed-emergency")}
              disabled={loading}
            >
              未处理紧急
            </button>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => applyQuickFilter("unprocessed-security")}
              disabled={loading}
            >
              未处理安防
            </button>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => applyQuickFilter("unprocessed-overspeed")}
              disabled={loading}
            >
              未处理超速
            </button>
            <button
              type="button"
              className="btn btn-outline-warning btn-sm"
              onClick={() => applyQuickFilter("unknown-state")}
              disabled={loading}
            >
              状态未知
            </button>
          </div>

          {error && <div className="status-banner error mt-3">{error}</div>}
          {optionError && <div className="status-banner warn mt-3">{optionError}</div>}
          {message && <div className="status-banner info mt-3">{message}</div>}

          <div className="table-responsive mt-3">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>时间</th>
                  <th>设备号</th>
                  <th>车牌号</th>
                  <th>客户</th>
                  <th>报警类型</th>
                  <th>速度</th>
                  <th>坐标</th>
                  <th>位置</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={10}>
                      <div className="status-banner empty m-0">暂无报警日志</div>
                    </td>
                  </tr>
                )}
                {items.map((row) => {
                  const rowClearState = normalizeClearState(row);
                  const rowClearStateText = getClearStateText(row);

                  return (
                    <tr key={row.id}>
                      <td>{formatDateTimeText(row.gpsTime)}</td>
                      <td>
                        {row.deviceNo ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 align-baseline"
                            onClick={() => openMonitor(row)}
                            title="跳到监控页并定位该设备"
                          >
                            {row.deviceNo}
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{row.vehicleNo || "-"}</td>
                      <td>{row.customerName || `#${row.customerId ?? "-"}`}</td>
                      <td>{row.alarmTypeText || "-"}</td>
                      <td>{formatSpeedText(row.speed)}</td>
                      <td>{formatCoordinate(row.longitude, row.latitude)}</td>
                      <td style={{ minWidth: 220 }}>{row.positionDesc || "-"}</td>
                      <td>
                        <span className={`badge ${getClearStateBadgeClass(rowClearState)}`}>
                          {rowClearStateText}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => openMonitor(row)}
                          >
                            监控定位
                          </button>
                          {rowClearState !== "processed" ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openClearModal(row)}
                            >
                              标记已处理
                            </button>
                          ) : (
                            <span className="text-muted align-self-center">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted" style={{ fontSize: 13 }}>
              共 {total} 条，当前第 {page}/{pageCount} 页
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                disabled={page >= pageCount || loading}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>

      <ActionModal
        open={clearModal.open}
        title="处理报警记录"
        message={clearModal.row ? `确认将设备 ${clearModal.row.deviceNo || "-"} 的报警记录标记为已处理吗？` : ""}
        fields={[]}
        initialValues={{}}
        confirmText="确认处理"
        submitting={modalSubmitting}
        onCancel={() => setClearModal({ open: false, row: null })}
        onConfirm={submitClearAlarm}
      />
    </div>
  );
}
